import React from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@cbrebuild/blocks';

import RowErrorBoundary from '../../components/error-boundary/row-error-boundary';
import { getAvatarColor } from '../../utils/avatar-utils';

// Utility functions to safely render commission user info
const getCommissionUserName = (user) => {
  if (!user || typeof user !== 'object') return 'Unknown User';
  if (user.first_name || user.last_name) return `${user.first_name || ''} ${user.last_name || ''}`.trim();
  if (user.username) return user.username;
  return 'Unknown User';
};

const formatCommissionAmount = (amount) => {
  if (!amount && amount !== 0) return '$0';
  const numericAmount = parseFloat(amount);
  if (isNaN(numericAmount)) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(numericAmount);
};

const formatPercentage = (percentage) => {
  if (!percentage && percentage !== 0) return '0%';
  const numericPercentage = parseFloat(percentage);
  if (isNaN(numericPercentage)) return '0%';
  return `${numericPercentage.toFixed(2)}%`;
};

const AdminCommissionOverview = ({
  deal,
  formatDate,
  formatCurrency,
}) => {
  return (
    <RowErrorBoundary>
      {/* Commission Overview Section */}
      <div className="collaborators-section">
        <div className="collaborators-header">
          <span className="collaborators-label">
            <Icon iconName="dollar-sign" />
            Commission Overview
          </span>
        </div>
        
        <div className="collaborators-subsection">
          {/* Financial Summary */}
          <div className="commission-overview-section">
            <div className="deal-financial-summary">
              <div className="financial-row">
                <div className="financial-item">
                  <div className="financial-label">RENT:</div>
                  <div className="financial-value">{formatCurrency(deal.rent_amount)}</div>
                </div>
                <div className="financial-item">
                  <div className="financial-label">DEAL VALUE:</div>
                  <div className="financial-value">{formatCurrency(deal.deal_value)}</div>
                </div>
                <div className="financial-item">
                  <div className="financial-label">COMMISSION:</div>
                  <div className="financial-value">{formatCurrency(deal.commission_amount)}</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Commission Users */}
          {deal.commission_users && deal.commission_users.length > 0 ? (
            <div className="commission-users-grid">
              {deal.commission_users.map((commissionUser, index) => (
                <div key={`commission-${commissionUser.user_id || commissionUser.id || index}`} 
                     className="collaborator-item commission-user-item">
                  <div className="collaborator-avatar">
                    <div 
                      className="avatar-placeholder"
                      style={{ 
                        backgroundColor: getAvatarColor(
                          commissionUser.first_name, 
                          commissionUser.last_name, 
                          commissionUser.user_id || commissionUser.id
                        ) 
                      }}
                    >
                      {(commissionUser.first_name?.charAt(0) || '') + (commissionUser.last_name?.charAt(0) || '')}
                    </div>
                  </div>
                  <div className="collaborator-details">
                    <div className="collaborator-name">
                      {getCommissionUserName(commissionUser)}
                      {commissionUser.role && <span className="role-badge commission-role-badge">{commissionUser.role}</span>}
                    </div>
                    <div className="collaborator-info">
                      {commissionUser.work_email || commissionUser.email || 'No email available'}
                    </div>
                    {(commissionUser.commission_amount || commissionUser.commission_percentage) && (
                      <div className="commission-details">
                        {commissionUser.commission_amount && (
                          <div className="commission-amount">
                            <Icon iconName="dollar-sign" />
                            <span>{formatCommissionAmount(commissionUser.commission_amount)}</span>
                          </div>
                        )}
                        {commissionUser.commission_percentage && (
                          <div className="commission-percentage">
                            <Icon iconName="percent" />
                            <span>{formatPercentage(commissionUser.commission_percentage)}</span>
                          </div>
                        )}
                      </div>
                    )}
                    {commissionUser.team_name && (
                      <div className="team-info">
                        <Icon iconName="users" />
                        <span>{commissionUser.team_name}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-collaborators">
              <Icon iconName="dollar-sign" />
              <span>No commission users found for this deal</span>
            </div>
          )}
          
          {/* Additional Commission Information */}
          {(deal.commission_due_date || deal.commission_status || deal.commission_notes) && (
            <div className="commission-additional-info">
              <div className="commission-info-grid">
                {deal.commission_due_date && (
                  <div className="info-item">
                    <div className="info-label">COMMISSION DUE DATE</div>
                    <div className="info-value">{formatDate(deal.commission_due_date)}</div>
                  </div>
                )}
                {deal.commission_status && (
                  <div className="info-item">
                    <div className="info-label">COMMISSION STATUS</div>
                    <div className="info-value">
                      <span className={`status-badge status-${deal.commission_status?.toLowerCase().replace(/\s+/g, '-')}`}>
                        {deal.commission_status}
                      </span>
                    </div>
                  </div>
                )}
                {deal.commission_notes && (
                  <div className="info-item commission-notes-item">
                    <div className="info-label">COMMISSION NOTES</div>
                    <div className="info-value commission-notes">{deal.commission_notes}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </RowErrorBoundary>
  );
};

AdminCommissionOverview.propTypes = {
  deal: PropTypes.shape({
    commission_users: PropTypes.arrayOf(PropTypes.shape({
      user_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      work_email: PropTypes.string,
      email: PropTypes.string,
      role: PropTypes.string,
      commission_amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      commission_percentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      team_name: PropTypes.string,
    })),
    rent_amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    deal_value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    commission_amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    commission_due_date: PropTypes.string,
    commission_status: PropTypes.string,
    commission_notes: PropTypes.string,
  }).isRequired,
  formatDate: PropTypes.func.isRequired,
  formatCurrency: PropTypes.func.isRequired,
};

export default AdminCommissionOverview;
